import type { ReactNode } from 'react';
import {
  PostureLegend,
  PostureUnitBar,
  ReadinessGauge,
  TrendChart,
  type TrendPoint,
} from '@/components/ui/charts';
import type { PostureStatus } from '@/components/ui/posture-chip';
import type { Recommendation } from '@/lib/api/health-readiness';
import {
  ROLLUP_LABEL,
  ROLLUP_TO_POSTURE,
  type HealthReadinessReport,
  type ReportFigures,
  type ReportVerdict,
} from '@/lib/api/health-readiness-report';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import { BAND_BG_CLASS } from '../model';
import { localDayIso } from '../overview/overview-model';
import { OVERLINE } from '../overview/parts';

/**
 * The report's cover (T-0245; prototype `reportTab()` → `.cover`): the
 * title, the as-of line, and three figures — the program posture (its
 * word, one cell per domain worst first, the counts), the go-live
 * readiness gauge, and readiness over time. Every number is the API's: the
 * verdict (the derivers' posture and weighted total) and the figures (the
 * composite read's domains and trend), live on a draft and frozen on a
 * published report. The PDF draws the same three from the same frozen
 * rollups (`packages/pdf-render/src/figures.ts`).
 */

const FIGURE_POSTURE: Record<ReportFigures['domains'][number]['posture'], PostureStatus> = {
  critical: 'critical',
  at_risk: 'at-risk',
  healthy: 'healthy',
  unassessed: 'unassessed',
};

function isRecommendation(v: string | null): v is Recommendation {
  return v === 'go' || v === 'conditional_go' || v === 'no_go';
}

/**
 * The cover's trend: the snapshot totals, then the current point — today's
 * live total on a draft, the total at publish on a published report —
 * unless a snapshot was captured that same day (as the Overview does).
 * Placement only; the totals are the API's.
 */
export function reportTrendPoints(
  figures: ReportFigures | null,
  verdict: ReportVerdict,
  report: HealthReadinessReport,
  today: Date,
): TrendPoint[] {
  const points: TrendPoint[] = (figures?.trend ?? [])
    .filter((t) => t.total_percent !== null)
    .map((t) => ({ date: t.captured_at, value: t.total_percent as number }));
  const readiness = verdict.readiness;
  if (!readiness || readiness.scored_count === 0 || readiness.total_percent === null) {
    return points;
  }
  const published = report.status !== 'draft';
  const at = published ? report.published_at : localDayIso(today);
  if (!at) return points;
  const last = points[points.length - 1];
  if (last && formatDate(last.date) === formatDate(at)) return points;
  points.push({
    date: at,
    value: readiness.total_percent,
    live: !published,
    sub: published ? 'Frozen at publish' : 'Live; Publish freezes it',
  });
  return points;
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      aria-label={label}
      className="flex min-w-0 flex-col gap-2 rounded-card border border-ink-100 p-3"
    >
      <span className={OVERLINE}>{label}</span>
      {children}
    </section>
  );
}

function NotInScope({ children }: { children: ReactNode }) {
  return <p className="text-body-sm text-ink-500">{children}</p>;
}

export interface ReportCoverProps {
  report: HealthReadinessReport;
  verdict: ReportVerdict;
  figures: ReportFigures | null;
  /** "Client · Engagement", when the engagement has loaded. */
  title?: string | null;
  today?: Date;
}

export function ReportCover({
  report,
  verdict,
  figures,
  title,
  today = new Date(),
}: ReportCoverProps) {
  const draft = report.status === 'draft';
  const asOf = draft ? formatDate(localDayIso(today)) : formatDate(report.published_at);
  const posture = verdict.posture;
  const status = posture ? ROLLUP_TO_POSTURE[posture.overall_rollup] : null;
  const readiness = verdict.readiness;

  return (
    <div className="flex flex-col gap-1.5 border-b border-ink-100 pb-5" data-testid="report-cover">
      <span className={OVERLINE}>Health &amp; readiness report</span>
      {title ? <div className="t-display">{title}</div> : null}
      <div className="t-meta">
        As of {asOf} ·{' '}
        {draft
          ? 'the figures below are the live rollups; Publish freezes them'
          : 'the figures below are the rollups, frozen at publish'}
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1fr_1.4fr]">
        <Figure label="Program posture">
          {posture && status ? (
            <>
              <div
                className="flex items-center gap-2.5 text-[24px] font-semibold leading-[30px] tracking-[-0.015em] text-ink-900"
                data-testid="cover-posture"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-3 w-3 flex-none rounded-full',
                    status === 'unassessed' ? 'bg-ink-200' : BAND_BG_CLASS[status],
                  )}
                />
                {ROLLUP_LABEL[posture.overall_rollup]}
              </div>
              {figures ? (
                <>
                  <PostureUnitBar
                    motionKey="report"
                    units={figures.domains.map((d) => ({
                      id: d.id,
                      name: d.name,
                      posture: FIGURE_POSTURE[d.posture],
                    }))}
                  />
                  <PostureLegend
                    counts={{
                      critical: figures.posture_counts.critical,
                      'at-risk': figures.posture_counts.at_risk,
                      healthy: figures.posture_counts.healthy,
                      unassessed: figures.posture_counts.unassessed,
                    }}
                  />
                </>
              ) : (
                <p className="t-meta">Published before the report froze its domain figures.</p>
              )}
            </>
          ) : (
            <NotInScope>Health Check is not in scope.</NotInScope>
          )}
        </Figure>
        <Figure label="Go-live readiness">
          {readiness ? (
            <>
              <ReadinessGauge
                small
                motionKey="report"
                value={readiness.scored_count > 0 ? readiness.total_percent : null}
                thresholds={readiness.thresholds}
                verdict={
                  isRecommendation(readiness.recommendation) ? readiness.recommendation : null
                }
                scored={readiness.scored_count}
                total={readiness.criteria_count}
              />
              <p className="t-meta text-center">
                {readiness.scored_count} of {readiness.criteria_count} criteria scored · Go at{' '}
                {readiness.thresholds.go}% · Conditional at {readiness.thresholds.conditional}%
              </p>
            </>
          ) : (
            <NotInScope>Go-Live Readiness is not in scope.</NotInScope>
          )}
        </Figure>
        <Figure label="Readiness over time">
          {readiness ? (
            <TrendChart
              motionKey="report"
              points={reportTrendPoints(figures, verdict, report, today)}
              thresholds={readiness.thresholds}
              width={420}
              height={170}
            />
          ) : (
            <NotInScope>Go-Live Readiness is not in scope.</NotInScope>
          )}
        </Figure>
      </div>
    </div>
  );
}
